import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import carBackground from '../assets/auto-main.jpeg';
import TariffModal from '../components/TariffModal';
import CheckIcon from '../components/icons/CheckIcon';
import { apiService } from '../services/api';
import { getDeviceFingerprint } from '../utils/fingerprint';
import { useNavigate } from 'react-router-dom';
import { createAntiBotBundle } from '../utils/antibot';
import tariffsData from '../data/tariffs.json';

// Styled Components
const AutoPageWrapper = styled.div`
  /* Remove padding to eliminate unwanted white space under the fixed header */
  padding-top: 0px;
`;

const HeroSection = styled.section`
  min-height: 80vh;
  background: url(${carBackground}) center/cover no-repeat;
  display: flex;
  align-items: center;
  color: white;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.2);
    z-index: 1;
  }
`;

const HeroContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  width: 100%;
`;

const HeroContent = styled.div`
    max-width: 600px;
    animation: slideInLeft 1s ease-out;
    position: relative;
    z-index: 2;
`;

const HeroTitle = styled.h2`
  font-size: 3rem;
  font-weight: 700;
  color: white;
  margin-bottom: 2rem;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, var(--primary-orange) 0%, #ff6b35 100%);
  color: white;
  border: none;
    margin-top: 2rem;
  padding: 18px 40px;
  font-size: 1.2rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(236, 92, 44, 0.3);
  position: relative;
  overflow: hidden;
  animation: fadeInUp 0.8s ease-out 0.4s both;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(236, 92, 44, 0.4);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 15px 30px;
    font-size: 1.1rem;
  }
`;

const PlanDemoButton = styled.button`
  background: linear-gradient(45deg, var(--primary-orange), #ff7043);
  color: white;
  border: none;
  padding: 18px 40px;
  font-size: 1.2rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(236, 92, 44, 0.3);
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(236, 92, 44, 0.4);
  }
`;

// Simple pricing table styles
const PricingTableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const PricingTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
`;

const PricingHeadCell = styled.th`
  text-align: left;
  padding: 14px 16px;
  background: #fafafa;
  font-weight: 700;
  color: #222;
  border-bottom: 1px solid rgba(0,0,0,0.06);
`;

const PricingCell = styled.td`
  padding: 14px 16px;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  vertical-align: top;
`;

const PricingSectionRow = styled.tr`
  background: #fff3ec;
`;

const PricingSectionCell = styled.td`
  padding: 12px 16px;
  font-weight: 700;
  color: #cc4100;
  border-bottom: 1px solid rgba(0,0,0,0.06);
`;

const BenefitsSection = styled.section`
  padding: 4rem 0;
  background: white;
`;

const BenefitsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  text-align: center;
`;

const BenefitBox = styled.div`
  padding: 2rem;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const BenefitIcon = styled.i`
  font-size: 2.5rem;
  color: var(--primary-orange);
  margin-bottom: 1rem;
  display: block;
`;

const BenefitText = styled.span`
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
`;

const AutoDetailsSection = styled.section`
  padding: 5rem 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const AutoDetailsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.header`
  text-align: center;
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h4`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary-blue);
  margin-bottom: 2rem;
`;

const SectionDescription = styled.span`
  font-size: 1.2rem;
  line-height: 1.8;
  color: #555;
  max-width: 800px;
  margin: 0 auto;
  display: block;
`;

const MoreText = styled.div<{ isVisible: boolean }>`
  margin-top: 2rem;
  overflow: hidden;
  max-height: ${props => props.isVisible ? '1000px' : '0'};
  opacity: ${props => props.isVisible ? '1' : '0'};
  transition: all 0.5s ease;
`;

const ReadMoreButton = styled.button`
  margin-top: 2rem;
  background: var(--primary-orange);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--primary-blue);
    transform: translateY(-2px);
  }
`;

const AutoPackagesSection = styled.section`
  padding: 5rem 0;
  background: rgba(248, 249, 250, 0.5);
`;

const PackagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const PackageCard = styled.div`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(236, 92, 44, 0.2);
  }
`;

const CardHeader = styled.div`
  background: linear-gradient(45deg, var(--primary-orange), #ff7043);
  color: white;
  padding: 1.5rem;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
`;

const CardBody = styled.div`
  padding: 2rem;
`;

const Price = styled.p`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary-orange);
  text-align: center;
  margin-bottom: 1.5rem;
`;

const FeatureList = styled.div`
  margin-bottom: 2rem;
`;

const FeatureItem = styled.p`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  
  i {
    color: #28a745;
    margin-right: 0.75rem;
    font-size: 1.2rem;
  }
`;

const ProeflesButton = styled.button`
  width: 100%;
  background: var(--primary-blue);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--primary-orange);
    transform: translateY(-2px);
  }
`;

const CallMeBackSection = styled.section`
  padding: 5rem 0;
  background: linear-gradient(135deg, var(--primary-blue) 0%, #1e40af 100%);
  color: white;
`;

const ContactForm = styled.form`
  max-width: 600px;
  margin: 0 auto;
  padding: 3rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const FormTitle = styled.h4`
  text-align: center;
  margin-bottom: 2rem;
  font-size: 2rem;
  font-weight: 600;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 1rem 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }
  
  &:focus {
    outline: none;
    border-color: var(--primary-orange);
    background: rgba(255, 255, 255, 0.2);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 1rem 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary-orange);
    background: rgba(255, 255, 255, 0.2);
  }
  
  option {
    background: var(--primary-blue);
    color: white;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: var(--primary-orange);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    background: #d44d26;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(236, 92, 44, 0.3);
  }
`;

const Auto: React.FC = () => {
  const { t } = useTranslation();
    const navigate = useNavigate();

    const [showMoreText, setShowMoreText] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    telephone: '',
    courseType: 'auto'
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<{
    price: string;
    features: string[];
    sectionTitle: string;
  } | null>(null);

  const handlePackageSelect = (price: string, features: string[], sectionTitle: string) => {
    setSelectedPackage({ price, features, sectionTitle });
    setIsModalOpen(true);
  };

    const goToDemoLesson = () => {
        navigate('/');
    };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPackage(null);
  };

    const toggleMoreText = () => {
    setShowMoreText(!showMoreText);
  };

  // Dutch phone number validation
  const isValidDutchPhoneNumber = (phone: string): boolean => {
    const cleanPhone = phone.replace(/\s|-/g, '');
    const dutchMobilePattern = /^(\+31|0031|0)6\d{8}$/;
    const dutchLandlinePattern = /^(\+31|0031|0)[1-9]\d{7,8}$/;
    return dutchMobilePattern.test(cleanPhone) || dutchLandlinePattern.test(cleanPhone);
  };

  const [minTimeReached, setMinTimeReached] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [antiBot] = useState(() => createAntiBotBundle());
  const [submitLocked, setSubmitLocked] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMinTimeReached(true), 3000);
    return () => clearTimeout(t);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { name, telephone, courseType } = formData;
    const language = 'nl'; // You can get this from i18n context

    // Validation
    const ERROR_INPUT_MESSAGE = "Vul alle vereiste velden in.";
    if (name.trim().length < 2 || !isValidDutchPhoneNumber(telephone)) {
      alert(ERROR_INPUT_MESSAGE);
      return;
    }

    if (!minTimeReached || submitLocked) {
      alert('Wacht een paar seconden voordat je het formulier indient.');
      return;
    }

    if (honeypot && honeypot.trim().length > 0) {
      // Silently ignore bots
      return;
    }

    try {
      setSubmitLocked(true);
      const deviceFingerprint = await getDeviceFingerprint();
      const payload = {
        name,
        email: '', // no email field in this form
        phone: telephone,
        message: `Course Type: ${courseType}`,
        language,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        deviceFingerprint,
        submissionId: antiBot.submissionId,
        minTimeMs: antiBot.getMinTimeMs(),
        honeypotFieldName: antiBot.honeypotFieldName,
        honeypotValue: honeypot,
        [antiBot.honeypotFieldName]: honeypot,
      };
      const resp = await apiService.post<any>('/api/public/email/call-me-back', payload);
      const msg = (resp && (resp.message || resp.msg)) || 'Bedankt! Je verzoek is verzonden.';
      alert(msg);
      setFormData({ name: '', telephone: '', courseType: 'auto' });
    } catch (error) {
      // Intentionally avoid console.error to keep browser console clean
      const anyErr: any = error;
      if (anyErr?.status === 429) {
        alert('Je hebt de indieningslimiet bereikt. Neem contact met ons op via telefoon of WhatsApp.');
      } else {
        const errorMessage = 'Er is iets misgegaan. Probeer het opnieuw.';
        alert(errorMessage);
      }
    } finally {
      setSubmitLocked(false);
    }
  };

  const scrollToContact = () => {
    document.getElementById('call-me-back')?.scrollIntoView({ behavior: 'smooth' });
  };

  const packages = [
    { nameKey: 'auto.packages.pro_max', price: '€2850,-', lessonsKey: 'auto.packages.lessons_40', examKey: 'auto.packages.cbr_exam' },
    { nameKey: 'auto.packages.pro', price: '€2240,-', lessonsKey: 'auto.packages.lessons_30', examKey: 'auto.packages.cbr_exam' },
    { nameKey: 'auto.packages.premium', price: '€1620,-', lessonsKey: 'auto.packages.lessons_20', examKey: 'auto.packages.cbr_exam' }
  ];


    const handleCTAClick = () => {
        navigate('/#demo-lesson');
    };

    return (
    <AutoPageWrapper>
      {/* Hero Section */}
      <HeroSection>
        <HeroContainer>
          <HeroContent>
            <HeroTitle>{t('auto.hero.title')}</HeroTitle>
              <CTAButton onClick={handleCTAClick}>
                  {t('intro.button')}
              </CTAButton>
          </HeroContent>
        </HeroContainer>
      </HeroSection>
        
      {/* Auto Details Section */}
      <AutoDetailsSection>
        <AutoDetailsContainer>
          <SectionHeader>
            <SectionTitle>{t('auto.details.title')}</SectionTitle>
            <SectionDescription>
              <strong>{t('auto.details.intro')}</strong><br /><br />
              {t('auto.details.description')}
            </SectionDescription>
            
            <MoreText isVisible={showMoreText}>
              <SectionDescription>
                {t('auto.details.more_text').split('\n\n').map((paragraph, index) => (
                  <span key={index}>
                    {paragraph}
                    {index < t('auto.details.more_text').split('\n\n').length - 1 && <><br /><br /></>}
                  </span>
                ))}
              </SectionDescription>
            </MoreText>
            
            <ReadMoreButton onClick={toggleMoreText}>
              {showMoreText ? t('auto.details.read_less') : t('auto.details.read_more')}
            </ReadMoreButton>
          </SectionHeader>
        </AutoDetailsContainer>
      </AutoDetailsSection>

      {/* Auto Packages Section */}
      <AutoPackagesSection>
        <AutoDetailsContainer>
          <SectionHeader>
            <SectionTitle>{t('auto.packages.title')}</SectionTitle>
          </SectionHeader>
          
          <PackagesGrid>
            {packages.map((pkg, index) => (
              <PackageCard key={index}>
                <CardHeader>{t(pkg.nameKey)}</CardHeader>
                <CardBody>
                  <Price>{pkg.price}</Price>
                  <FeatureList>
                    <FeatureItem>
                      <CheckIcon aria-hidden="true" />
                      {t(pkg.lessonsKey)}
                    </FeatureItem>
                    <FeatureItem>
                      <CheckIcon aria-hidden="true" />
                      {t(pkg.examKey)}
                    </FeatureItem>
                  </FeatureList>
                  <ProeflesButton onClick={() => handlePackageSelect(
                    pkg.price, 
                    [t(pkg.lessonsKey), t(pkg.examKey)], 
                    t('auto.packages.title')
                  )}>
                    {t('tariffs.packages.choose')}
                  </ProeflesButton>
                </CardBody>
              </PackageCard>
            ))}
          </PackagesGrid>
        </AutoDetailsContainer>
      </AutoPackagesSection>

      {/* Call Me Back Section */}
      <CallMeBackSection id="call-me-back">
        <AutoDetailsContainer>
          <ContactForm onSubmit={handleSubmit}>
            <FormTitle>{t('auto.callback.title')}</FormTitle>
            {/* Honeypot field (hidden, randomized name) */}
            <input
              type="text"
              name={antiBot.honeypotFieldName}
              value={honeypot}
              onChange={(e) => {
                setHoneypot(e.target.value);
                antiBot.setHoneypotValue(e.target.value);
              }}
              style={{ display: 'none' }}
              tabIndex={-1}
              autoComplete="off"
            />
            
            <FormGroup>
              <FormInput
                type="text"
                name="name"
                placeholder={t('auto.callback.name') || 'Naam'}
                value={formData.name}
                onChange={handleInputChange}
                required
                minLength={2}
                maxLength={20}
              />
            </FormGroup>
            
            <FormGroup>
              <FormInput
                type="tel"
                name="telephone"
                placeholder={t('auto.callback.phone') || 'Telefoon'}
                value={formData.telephone}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <FormSelect
                name="courseType"
                value={formData.courseType}
                onChange={handleInputChange}
              >
                <option value="auto">{t('auto.callback.auto')}</option>
                <option value="motor">{t('auto.callback.motor')}</option>
              </FormSelect>
            </FormGroup>
            
            <SubmitButton type="submit">
              {t('auto.callback.submit')}
              <i className="fa fa-long-arrow-right" />
            </SubmitButton>
          </ContactForm>
        </AutoDetailsContainer>
      </CallMeBackSection>
      
      <TariffModal 
        isOpen={isModalOpen}
        onClose={closeModal}
        packageInfo={selectedPackage}
      />
    </AutoPageWrapper>
  );
};

export default Auto;

